import { useEffect, useMemo, useState } from "react";
import {
  listWithdrawalHistory,
  type WithdrawalHistoryItem,
  type WithdrawalHistoryResponse,
} from "@/lib/api";
import { formatAmount, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Badge, statusColor } from "@/components/DataTable";
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";

const METHOD_LABELS: Record<string, string> = {
  airtel: "Airtel Money",
  orange: "Orange Money",
  mpesa: "M-Pesa (Vodacom)",
  vodacom: "M-Pesa (Vodacom)",
  afrimoney: "Afrimoney",
};

const PAGE_SIZE = 20;

export function WithdrawalHistoryPage() {
  const [data, setData] = useState<WithdrawalHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<WithdrawalHistoryItem | null>(null);

  const load = async (p = page) => {
    setLoading(true);
    setError(null);
    try {
      const r = await listWithdrawalHistory({ page: p, limit: PAGE_SIZE });
      setData(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(page); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [page]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.transactions.filter((t) => {
      if (statusFilter && t.status.toLowerCase() !== statusFilter.toLowerCase()) return false;
      if (methodFilter && t.method.toLowerCase() !== methodFilter.toLowerCase()) return false;
      return true;
    });
  }, [data, statusFilter, methodFilter]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Historique des retraits"
        subtitle="Vos retraits mobile money, avec la ventilation des wallets réellement débités."
      />

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3 items-center">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Tous statuts</option>
            <option value="Successful">Successful</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
            <option value="Submitted">Submitted</option>
          </select>
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Toutes méthodes</option>
            {Object.entries(METHOD_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <div className="ml-auto text-xs text-[#7e8299]">
            {data ? `${data.pagination.total} retraits au total` : ""}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#7e8299]"><Loader2 className="inline animate-spin mr-2" /> Chargement…</div>
        ) : error ? (
          <div className="p-6 text-danger-700">Erreur : {error}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-[#7e8299]">Aucun retrait pour l'instant.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-[#7e8299]">
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Méthode demandée</th>
                <th className="px-6 py-3">Wallets débités</th>
                <th className="px-6 py-3 text-right">Montant</th>
                <th className="px-6 py-3">Statut</th>
                <th className="px-6 py-3">Référence</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(t)}>
                  <td className="px-6 py-3 text-[#3f4254]">{t.created_at ? formatDate(t.created_at) : "—"}</td>
                  <td className="px-6 py-3">
                    <span className="capitalize font-medium text-[#181c32]">{METHOD_LABELS[t.method] ?? t.method}</span>
                  </td>
                  <td className="px-6 py-3">
                    {t.source_wallets.length === 0 ? (
                      <span className="text-[#7e8299] text-xs">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {t.source_wallets.map((w) => (
                          <span
                            key={w}
                            className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                              w === t.method
                                ? "bg-slate-100 text-slate-700 border border-slate-200"
                                : "bg-amber-100 text-amber-800 border border-amber-200"
                            }`}
                            title={w === t.method ? "Wallet correspondant à la méthode demandée" : "Wallet différent — biggest-first strategy"}
                          >
                            {METHOD_LABELS[w] ?? w}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right font-mono font-semibold text-[#181c32]">
                    {formatAmount(Math.round(t.amount * 100), t.currency)}
                  </td>
                  <td className="px-6 py-3"><Badge color={statusColor(t.status.toLowerCase())}>{t.status}</Badge></td>
                  <td className="px-6 py-3 font-mono text-xs text-[#3f4254]">{t.reference || "—"}</td>
                  <td className="px-6 py-3 text-right text-[#7e8299]">→</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {data && data.pagination.pages > 1 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between">
            <div className="text-xs text-[#7e8299]">
              Page {data.pagination.page} / {data.pagination.pages}
            </div>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} /> Précédent
              </button>
              <button
                disabled={page >= data.pagination.pages}
                onClick={() => setPage((p) => Math.min(data.pagination.pages, p + 1))}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Suivant <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && <WithdrawalDetailDrawer item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function WithdrawalDetailDrawer({ item, onClose }: { item: WithdrawalHistoryItem; onClose: () => void }) {
  const differsFromRequested = item.source_wallets.length > 1 || (item.source_wallets.length === 1 && item.source_wallets[0] !== item.method);
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <aside className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-xl">
        <header className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="text-base font-semibold text-[#181c32]">Détail du retrait</div>
            <div className="text-xs text-[#7e8299] font-mono">{item.reference || item.id}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100"><X size={16} /></button>
        </header>
        <div className="p-6 space-y-5 text-sm">
          <Section label="Statut">
            <Badge color={statusColor(item.status.toLowerCase())}>{item.status}</Badge>
          </Section>
          <Section label="Méthode demandée">
            <div className="capitalize font-semibold">{METHOD_LABELS[item.method] ?? item.method}</div>
          </Section>
          <Section label="Montant">
            <div className="font-mono font-semibold text-lg">{formatAmount(Math.round(item.amount * 100), item.currency)}</div>
          </Section>
          <Section label="Créé le">
            <div>{item.created_at ? formatDate(item.created_at) : "—"}</div>
          </Section>
          {item.updated_at && (
            <Section label="Complété le">
              <div>{formatDate(item.updated_at)}</div>
            </Section>
          )}

          <div className="border-t border-gray-100 pt-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-2">Plan de débit</div>
            {item.debit_plan.length === 0 ? (
              <div className="text-[#7e8299] text-sm">Aucun plan de débit enregistré.</div>
            ) : (
              <div className="rounded border border-gray-200 divide-y divide-gray-100">
                {item.debit_plan.map((p, i) => (
                  <div key={i} className="px-3 py-2 flex justify-between text-sm">
                    <div className="text-[#3f4254]">{METHOD_LABELS[p.method] ?? p.method}</div>
                    <div className="font-mono font-semibold text-[#181c32]">
                      {formatAmount(Math.round(p.amount * 100), item.currency).replace(` ${item.currency}`, "")}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {differsFromRequested && (
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <strong>Stratégie biggest-first :</strong> le wallet demandé ({METHOD_LABELS[item.method] ?? item.method}) n'avait pas assez de solde, le retrait a été financé depuis d'autres wallets. Votre montant net est intact.
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}
